// piano-frontend/src/components/LessonConfigDialog.js
import React, { useState, useEffect } from "react";
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';
import {
  DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Divider, FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, IconButton, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  FormControlLabel, Switch, TextField, TableContainer, Tooltip
} from "@mui/material";
import Dialog from "./AppDialog";
import { Delete, Add, Edit, Check, Close } from "@mui/icons-material";
import api from "../api/axios";
import dayjs from "dayjs";
import DateImpactPreview from "./DateImpactPreview";
import DateField from "./DateField";

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const DEFAULT_OVERRIDE = {
  date: dayjs().format("YYYY-MM-DD"),
  is_open: true,
  start_time: "12:00",
  end_time: "18:00",
  reason: ""
};

const DEFAULT_BLOCK = {
  teacher_id: null,
  day_of_week: 'MON',
  start_time: '14:00',
  end_time: '14:30',
  reason: ''
};

const dayNamesKor = { MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" };

function LessonConfigDialog({ open, onClose, onSave }) {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [tabIndex, setTabIndex] = useState(0);
  const [config, setConfig] = useState({
    weekday_start_time: "14:00",
    weekday_end_time: "22:00",
    weekend_start_time: "12:00",
    weekend_end_time: "17:00",
    slot_duration: 30
  });
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ ...DEFAULT_OVERRIDE });
  const [editingOverrideId, setEditingOverrideId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [practiceConfirmDialog, setPracticeConfirmDialog] = useState({
    open: false,
    lessonOverride: null
  });
  const [blockSchedules, setBlockSchedules] = useState([]);
  const [newBlock, setNewBlock] = useState({ ...DEFAULT_BLOCK });
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    if (open) {
      fetchConfig();
      fetchOverrides();
      fetchBlockSchedules();
      fetchTeachers();
      setEditingOverrideId(null);
    }
  }, [open]);

  const fetchConfig = async () => {
    try {
      const res = await api.get("/lesson-config");
      setConfig({
        weekday_start_time: res.data.weekday_start_time?.slice(0, 5) || "14:00",
        weekday_end_time: res.data.weekday_end_time?.slice(0, 5) || "22:00",
        weekend_start_time: res.data.weekend_start_time?.slice(0, 5) || "12:00",
        weekend_end_time: res.data.weekend_end_time?.slice(0, 5) || "17:00",
        slot_duration: res.data.slot_duration || 30
      });
    } catch (err) {
      console.error("설정 로드 실패:", err);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await api.get("/lesson-config/overrides");
      setOverrides(res.data);
    } catch (err) {
      console.error("예외 설정 로드 실패:", err);
    }
  };

  const fetchBlockSchedules = async () => {
    try {
      const res = await api.get("/block-schedules");
      setBlockSchedules(res.data);
    } catch (err) {
      console.error("블록 스케줄 로드 실패:", err);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await api.get("/teachers");
      setTeachers(res.data);
    } catch (err) {
      console.error("선생님 목록 로드 실패:", err);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      await api.put("/lesson-config", config);
      toast("레슨 설정이 저장되었습니다.");
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "저장 실패", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditOverrideClick = (o) => {
    setEditingOverrideId(o.id);
    setNewOverride({
      date: o.date,
      is_open: o.is_open,
      start_time: o.start_time ? o.start_time.slice(0, 5) : "12:00",
      end_time: o.end_time ? o.end_time.slice(0, 5) : "18:00",
      reason: o.reason || ""
    });
  };

  const handleCancelEdit = () => {
    setEditingOverrideId(null);
    setNewOverride({ ...DEFAULT_OVERRIDE, date: dayjs().format("YYYY-MM-DD") });
  };

  // 레슨 예외에 맞춰 연습실 예외를 동기화(있으면 수정, 없으면 생성)
  const syncPracticeOverride = async ({ date, is_open, start_time, end_time, reason }) => {
    const body = {
      date,
      is_open,
      start_time: is_open ? start_time : null,
      end_time: is_open ? end_time : null,
      lesson_restricted: true,
      reason: reason || null
    };
    const res = await api.get("/practice-config/overrides");
    const existing = res.data.find(p => p.date === date);
    if (existing) {
      await api.put(`/practice-config/overrides/${existing.id}`, body);
    } else {
      await api.post("/practice-config/overrides", body);
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.date) {
      toast("날짜를 선택해주세요.", "warning");
      return;
    }

    const payload = {
      date: newOverride.date,
      is_open: newOverride.is_open,
      start_time: newOverride.is_open ? newOverride.start_time : null,
      end_time: newOverride.is_open ? newOverride.end_time : null,
      reason: newOverride.reason || null
    };

    // 수정 모드: 레슨 예외 PUT + 연습실 예외 동기화 (날짜 고정)
    if (editingOverrideId) {
      try {
        await api.put(`/lesson-config/overrides/${editingOverrideId}`, payload);
        let syncMsg = "연습실 예외도 동기화되었습니다.";
        try {
          await syncPracticeOverride(payload);
        } catch (syncErr) {
          console.error("연습실 예외 동기화 실패:", syncErr);
          syncMsg = "단, 연습실 예외 동기화에 실패했습니다.";
        }
        toast(`예외 설정이 수정되었습니다.\n${syncMsg}`);
        handleCancelEdit();
        fetchOverrides();
        onSave?.();
      } catch (err) {
        toast(err?.response?.data?.message || "수정 실패", "error");
      }
      return;
    }

    // 추가 모드: 레슨 예외 POST 후 연습실 동기화 여부 확인
    try {
      await api.post("/lesson-config/overrides", payload);

      fetchOverrides();

      setPracticeConfirmDialog({
        open: true,
        lessonOverride: { ...newOverride }
      });

      setNewOverride({ ...DEFAULT_OVERRIDE, date: dayjs().format("YYYY-MM-DD") });

    } catch (err) {
      toast(err?.response?.data?.message || "추가 실패", "error");
    }
  };

  const handlePracticeConfirm = async (addPractice) => {
    if (addPractice && practiceConfirmDialog.lessonOverride) {
      const override = practiceConfirmDialog.lessonOverride;
      try {
        await api.post("/practice-config/overrides", {
          date: override.date,
          is_open: override.is_open,
          start_time: override.is_open ? override.start_time : null,
          end_time: override.is_open ? override.end_time : null,
          lesson_restricted: true,
          reason: override.reason || null
        });
        toast("레슨 및 연습실 예외 설정이 추가되었습니다.");
      } catch (err) {
        if (err?.response?.status === 409) {
          toast("레슨 예외 설정이 추가되었습니다.\n(연습실은 이미 해당 날짜에 예외 설정이 존재합니다.)", "info");
        } else {
          toast("레슨 예외 설정이 추가되었습니다.\n(연습실 예외 설정 추가 실패)", "warning");
        }
      }
    } else {
      toast("레슨 예외 설정이 추가되었습니다.");
    }
    
    setPracticeConfirmDialog({ open: false, lessonOverride: null });
    onSave?.();
  };

  const handleDeleteOverride = async (id) => {
    if (!(await confirm({ title: "예외 설정 삭제", message: "예외 설정을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      await api.delete(`/lesson-config/overrides/${id}`);
      fetchOverrides();
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "삭제 실패", "error");
    }
  };

  const handleAddBlockSchedule = async () => {
    if (!newBlock.teacher_id) {
      toast("선생님을 선택해주세요.", "warning");
      return;
    }

    if (newBlock.start_time >= newBlock.end_time) {
      toast("시작 시간은 종료 시간보다 빨라야 합니다.", "warning");
      return;
    }

    try {
      const res = await api.post("/block-schedules", {
        teacher_id: newBlock.teacher_id,
        day_of_week: newBlock.day_of_week,
        start_time: newBlock.start_time,
        end_time: newBlock.end_time,
        reason: newBlock.reason
      });

      toast(res.data?.message || "블록 스케줄이 등록되었습니다.");
      fetchBlockSchedules();
      setNewBlock({ ...DEFAULT_BLOCK });
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "블록 스케줄 등록 실패", "error");
    }
  };

  const handleDeleteBlockSchedule = async (id) => {
    if (!(await confirm({ title: "블록 스케줄 삭제", message: "블록 스케줄을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      await api.delete(`/block-schedules/${id}`);
      toast("블록 스케줄이 삭제되었습니다.");
      fetchBlockSchedules();
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "블록 스케줄 삭제 실패", "error");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>레슨 설정</DialogTitle>
        <DialogContent>
          <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
            <Tab label="기본 설정" />
            <Tab label="예외 날짜" />
            <Tab label="블록 스케줄" />
          </Tabs>

          {/* 탭 0: 기본 설정 */}
          {tabIndex === 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                여기서 저장하면 평일 시간이 월~금 모두에 일괄 적용됩니다. 금요일 등 요일별로 다르게
                설정하려면 관리자 페이지 &gt; 운영 설정 &gt; 운영 시간 설정(기본) 탭을 이용하세요.
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
                평일 (월~금)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>시작 시간</InputLabel>
                  <Select
                    value={config.weekday_start_time}
                    label="시작 시간"
                    onChange={(e) => setConfig({ ...config, weekday_start_time: e.target.value })}
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>종료 시간</InputLabel>
                  <Select
                    value={config.weekday_end_time}
                    label="종료 시간"
                    onChange={(e) => setConfig({ ...config, weekday_end_time: e.target.value })}
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                주말 (토요일)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>시작 시간</InputLabel>
                  <Select
                    value={config.weekend_start_time}
                    label="시작 시간"
                    onChange={(e) => setConfig({ ...config, weekend_start_time: e.target.value })}
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>종료 시간</InputLabel>
                  <Select
                    value={config.weekend_end_time}
                    label="종료 시간"
                    onChange={(e) => setConfig({ ...config, weekend_end_time: e.target.value })}
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                기본 설정 저장
              </Button>
            </Box>
          )}

          {/* 탭 1: 예외 날짜 설정 */}
          {tabIndex === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                특정 날짜에 기본 설정과 다른 시간을 적용할 수 있습니다.
              </Typography>

              <Box sx={{
                display: "flex",
                gap: 1,
                mb: 3,
                p: 2,
                bgcolor: darkMode ? 'grey.800' : '#f5f5f5',
                borderRadius: 1,
                flexWrap: "wrap",
                alignItems: "center"
              }}>
                <DateField
                  label="날짜"
                  size="small"
                  value={newOverride.date}
                  onChange={(date) => setNewOverride({ ...newOverride, date })}
                  disabled={!!editingOverrideId}
                  sx={{ width: 170 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newOverride.is_open}
                      onChange={(e) => setNewOverride({ ...newOverride, is_open: e.target.checked })}
                    />
                  }
                  label="레슨 가능"
                />
                {newOverride.is_open && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel shrink>시작</InputLabel>
                      <Select
                        value={newOverride.start_time}
                        label="시작"
                        onChange={(e) => setNewOverride({ ...newOverride, start_time: e.target.value })}
                        notched
                      >
                        {TIME_OPTIONS.map(time => (
                          <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel shrink>종료</InputLabel>
                      <Select
                        value={newOverride.end_time}
                        label="종료"
                        onChange={(e) => setNewOverride({ ...newOverride, end_time: e.target.value })}
                        notched
                      >
                        {TIME_OPTIONS.map(time => (
                          <MenuItem key={time} value={time}>{time}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <TextField
                  label="사유"
                  size="small"
                  value={newOverride.reason}
                  onChange={(e) => setNewOverride({ ...newOverride, reason: e.target.value })}
                  sx={{ minWidth: 120 }}
                />
                {editingOverrideId ? (
                  <>
                    <Tooltip title="수정 저장">
                      <IconButton color="primary" onClick={handleAddOverride} aria-label="수정 저장">
                        <Check />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="취소">
                      <IconButton onClick={handleCancelEdit} aria-label="취소">
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <IconButton color="primary" onClick={handleAddOverride}>
                    <Add />
                  </IconButton>
                )}
              </Box>

              {newOverride.date && (
                <Box sx={{ mb: 3, mt: -1 }}>
                  <DateImpactPreview
                    date={newOverride.date}
                    change={{
                      kind: "LESSON_OVERRIDE",
                      payload: {
                        is_open: newOverride.is_open,
                        start_time: newOverride.is_open ? newOverride.start_time : null,
                        end_time: newOverride.is_open ? newOverride.end_time : null
                      }
                    }}
                  />
                </Box>
              )}

              {overrides.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                  등록된 예외 설정이 없습니다.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>날짜</TableCell>
                      <TableCell>상태</TableCell>
                      <TableCell>시간</TableCell>
                      <TableCell>사유</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overrides.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>
                          {o.date}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({["일","월","화","수","목","금","토"][new Date(o.date).getDay()]})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={o.is_open ? "레슨 가능" : "레슨 불가"}
                            color={o.is_open ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {o.is_open ? (
                            o.start_time && o.end_time 
                              ? `${o.start_time?.slice(0,5)} ~ ${o.end_time?.slice(0,5)}`
                              : "기본값 적용"
                          ) : "-"}
                        </TableCell>
                        <TableCell>{o.reason || "-"}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => handleEditOverrideClick(o)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteOverride(o.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}

          {/* 탭 2: 블록 스케줄 */}
          {tabIndex === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                매주 특정 요일의 특정 시간대를 블록하여 회원이 예약할 수 없도록 설정합니다.
              </Typography>

              <Box sx={{
                display: "flex",
                gap: 1,
                mb: 3,
                p: 2,
                bgcolor: darkMode ? 'grey.800' : '#f5f5f5',
                borderRadius: 1,
                flexWrap: "wrap",
                alignItems: "center"
              }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>선생님</InputLabel>
                  <Select
                    value={newBlock.teacher_id || ''}
                    onChange={(e) => setNewBlock({ ...newBlock, teacher_id: e.target.value })}
                    label="선생님"
                  >
                    {teachers.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>요일</InputLabel>
                  <Select
                    value={newBlock.day_of_week}
                    onChange={(e) => setNewBlock({ ...newBlock, day_of_week: e.target.value })}
                    label="요일"
                  >
                    {Object.entries(dayNamesKor).map(([eng, kor]) => (
                      <MenuItem key={eng} value={eng}>{kor}요일</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel shrink>시작</InputLabel>
                  <Select
                    value={newBlock.start_time}
                    label="시작"
                    onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                    notched
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel shrink>종료</InputLabel>
                  <Select
                    value={newBlock.end_time}
                    label="종료"
                    onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                    notched
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="사유"
                  size="small"
                  value={newBlock.reason}
                  onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                  sx={{ minWidth: 120 }}
                />

                <IconButton color="primary" onClick={handleAddBlockSchedule}>
                  <Add />
                </IconButton>
              </Box>

              {blockSchedules.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                  등록된 블록 스케줄이 없습니다.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>선생님</TableCell>
                        <TableCell>요일</TableCell>
                        <TableCell>시간</TableCell>
                        <TableCell>사유</TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {blockSchedules.map(bs => (
                        <TableRow key={bs.id}>
                          <TableCell>{bs.teacher?.name}</TableCell>
                          <TableCell>{dayNamesKor[bs.day_of_week]}요일</TableCell>
                          <TableCell>{bs.start_time.slice(0, 5)} ~ {bs.end_time.slice(0, 5)}</TableCell>
                          <TableCell>{bs.reason || '-'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleDeleteBlockSchedule(bs.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 연습실 예외 설정 추가 확인 다이얼로그 */}
      <Dialog 
        open={practiceConfirmDialog.open} 
        onClose={() => handlePracticeConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>연습실 예외 설정</DialogTitle>
        <DialogContent>
          <Typography>
            연습실도 레슨 일정에 맞게 예외 일정을 추가할까요?
          </Typography>
          {practiceConfirmDialog.lessonOverride && (
            <Box sx={{ mt: 2, p: 2, bgcolor: darkMode ? 'grey.800' : '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>날짜:</strong> {practiceConfirmDialog.lessonOverride.date}
              </Typography>
              <Typography variant="body2">
                <strong>상태:</strong> {practiceConfirmDialog.lessonOverride.is_open ? "운영" : "휴무"}
              </Typography>
              {practiceConfirmDialog.lessonOverride.is_open && (
                <Typography variant="body2">
                  <strong>시간:</strong> {practiceConfirmDialog.lessonOverride.start_time} ~ {practiceConfirmDialog.lessonOverride.end_time}
                </Typography>
              )}
              {practiceConfirmDialog.lessonOverride.reason && (
                <Typography variant="body2">
                  <strong>사유:</strong> {practiceConfirmDialog.lessonOverride.reason}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePracticeConfirm(false)}>
            아니오
          </Button>
          <Button onClick={() => handlePracticeConfirm(true)} variant="contained">
            예
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LessonConfigDialog;